import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {PHONE_TOUCH} from 'src/components/mobile/mobileDesign.sx';

/**
 * What an action button is for, which is what decides its colour.
 *
 * - `primary` — the one thing the screen is for: add a row, advance a contract.
 * - `secondary` — an alternative the reader may want, offered without competing with the primary.
 * - `danger` — archiving, blocking, deleting. The raspberry accent, spent only here.
 */
type ActionTone = 'primary' | 'secondary' | 'danger';

const tones = (tone: ActionTone) => {
    switch (tone) {
        case 'secondary':
            return {
                color: theme.palette.primary.main,
                background: theme.palette.common.white,
                border: `1px solid ${theme.palette.primaryColor[100]}`,
                boxShadow: 'none',
                hover: {
                    background: theme.palette.primaryColor[50],
                    border: `1px solid ${theme.palette.primary.light}`,
                    boxShadow: 'none',
                },
                active: {boxShadow: 'none'},
            };
        case 'danger':
            return {
                color: theme.palette.common.white,
                background: `linear-gradient(180deg, ${theme.palette.brand.main} 0%, ${theme.palette.brand.dark} 100%)`,
                border: '1px solid transparent',
                boxShadow: '0 1px 2px rgba(189, 25, 73, 0.32)',
                hover: {
                    // A gradient again, not a flat colour. Going from a gradient to a colour drops
                    // background-image at once while background-color fades up from transparent, so
                    // the page showed through and the button blinked white under the cursor.
                    background: `linear-gradient(180deg, ${theme.palette.brand.dark} 0%, ${theme.palette.brand.dark} 100%)`,
                    border: '1px solid transparent',
                    boxShadow: '0 2px 6px rgba(189, 25, 73, 0.36)',
                },
                active: {boxShadow: '0 1px 1px rgba(189, 25, 73, 0.4)'},
            };
        default:
            return {
                color: theme.palette.common.white,
                background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                border: '1px solid transparent',
                boxShadow: '0 1px 2px rgba(0, 109, 199, 0.32)',
                hover: {
                    background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    border: '1px solid transparent',
                    boxShadow: '0 2px 6px rgba(0, 109, 199, 0.36)',
                },
                active: {boxShadow: '0 1px 1px rgba(0, 109, 199, 0.4)'},
            };
    }
};

/**
 * One shape for every action button in the product.
 *
 * They used to be written per screen, and it showed: on a single contract, "change my plan" was a
 * 44px pill, "archive" and "move to operation" were 5px rectangles, all three were the same
 * raspberry whatever they did, and the gaps above them were 4px and 20px. Same radius, same height,
 * same weight here — what varies is the tone, and the tone says what the button does.
 *
 * The lift is a tinted shadow rather than a heavy border: at this height a border competes with the
 * outlined controls next to it, which are genuinely outlined.
 */
export const actionButtonSx = (height: string, tone: ActionTone = 'primary'): SxProps<Theme> => {
    const t = tones(tone);

    return {
        height,
        minHeight: height,
        px: 1.75,
        borderRadius: '8px',
        fontSize: '0.8125rem',
        fontWeight: 600,
        letterSpacing: '0.01em',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        color: t.color,
        background: t.background,
        border: t.border,
        boxShadow: t.boxShadow,
        // Not `background`: a gradient does not interpolate, so a browser either snaps it or, worse,
        // tears it down mid-transition. Only the properties that genuinely animate.
        transition: 'box-shadow 150ms ease, border-color 150ms ease',
        // The height is the caller's choice, and every caller chose a desktop one - 36px, in places
        // 32px, against the 44px a fingertip actually needs. Below a desktop the floor is the same
        // everywhere, whatever was asked for, so no screen has to remember it.
        [theme.breakpoints.down('md')]: {
            height: PHONE_TOUCH,
            minHeight: PHONE_TOUCH,
            px: 2,
            borderRadius: '10px',
            fontSize: '0.875rem',
        },
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(0.75),
            marginLeft: theme.spacing(-0.25),
            '& .MuiSvgIcon-root': {
                fontSize: '1.125rem',
            },
        },
        // The label used to be a nested Typography that set its own colour, which is why a button
        // could look right in one place and wrong in another. The button owns its colour now.
        '& span': {
            textTransform: 'none',
        },
        '&:hover': t.hover,
        '&:active': t.active,
    };
};

/** The primary "add" action, wherever a list offers one. */
export const addActionButtonSx = (height: string): SxProps<Theme> => actionButtonSx(height, 'primary');
